import { useState } from 'react'
import SidebarToggle from '@/components/SidebarToggle'
import { DownloadIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { exportAdminReport } from '@/services/admin'
import { ApiError } from '@/services/api'
import FeedbackTab from './FeedbackTab'
import LoginLogsTab from './LoginLogsTab'
import MeetingsTab from './MeetingsTab'
import UsersTab from './UsersTab'
import ActivityTab from './ActivityTab'
import AllowedEmailsTab from './AllowedEmailsTab'
import { dateInputToEndMs, dateInputToStartMs } from './adminFormat'

type AdminTab =
  | 'users'
  | 'login-logs'
  | 'meetings'
  | 'activity'
  | 'feedback'
  | 'allowed-emails'

/**
 * Admin management screen. Renders the three read-only data tabs (mirroring the
 * Excel report) plus a Download Excel action that reuses the legacy export
 * endpoint. Tabs lazy-load: each mounts its data only once first opened.
 */
export default function AdminPage() {
  const { t } = useUiStrings()
  const [tab, setTab] = useState<AdminTab>('meetings')
  // Track which tabs have been opened so their data fetches only once needed.
  // The landing tab counts as visited from the start, so it fetches on mount.
  const [visited, setVisited] = useState<Record<AdminTab, boolean>>({
    meetings: true,
    feedback: false,
    users: false,
    'login-logs': false,
    activity: false,
    'allowed-emails': false,
  })
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  // One date range for the whole screen. It used to live inside each of the two
  // tabs that show it, which is why Export — a button in the header — could not
  // see it and always wrote the full history.
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const selectTab = (next: AdminTab) => {
    setTab(next)
    setVisited((current) => ({ ...current, [next]: true }))
  }

  const handleExport = async () => {
    if (exporting) return
    setExportError(null)
    setExporting(true)
    try {
      await exportAdminReport({
        dateFrom: dateInputToStartMs(dateFrom),
        dateTo: dateInputToEndMs(dateTo),
      })
    } catch (error) {
      setExportError(error instanceof ApiError ? error.message : t.exportReportError)
    } finally {
      setExporting(false)
    }
  }

  const tabs: { id: AdminTab; label: string }[] = [
    { id: 'meetings', label: t.adminTabMeetings },
    { id: 'feedback', label: t.adminTabFeedback },
    { id: 'users', label: t.adminTabUsers },
    { id: 'login-logs', label: t.adminTabLoginLogs },
    { id: 'activity', label: t.adminTabActivity },
    { id: 'allowed-emails', label: t.adminTabAllowedEmails },
  ]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 shadow-[var(--shadow-sm)] sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-[var(--text-primary)]">
          {t.adminManagement}
        </h1>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg bg-[var(--accent)] px-3 text-xs font-semibold text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-50"
        >
          <DownloadIcon className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">
            {exporting ? t.exportingReport : t.adminDownloadExcel}
          </span>
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          {exportError && (
            <p role="alert" className="mb-3 text-xs text-[var(--error)]">
              {exportError}
            </p>
          )}

          <div
            role="tablist"
            aria-label={t.adminManagement}
            className="flex gap-1 overflow-x-auto border-b border-[var(--border-hover)]"
          >
            {tabs.map((item) => {
              const active = tab === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectTab(item.id)}
                  className={[
                    '-mb-px shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors',
                    active
                      ? 'border-[var(--accent)] text-[var(--accent)]'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                  ].join(' ')}
                >
                  {item.label}
                </button>
              )
            })}
          </div>

          <div className="mt-5" role="tabpanel">
            {tab === 'users' && <UsersTab enabled={visited.users} />}
            {tab === 'activity' && (
              <ActivityTab
                enabled={visited.activity}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            )}
            {tab === 'login-logs' && (
              <LoginLogsTab
                enabled={visited['login-logs']}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            )}
            {tab === 'meetings' && (
              <MeetingsTab
                enabled={visited.meetings}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            )}
            {tab === 'feedback' && <FeedbackTab enabled={visited.feedback} />}
            {tab === 'allowed-emails' && (
              <AllowedEmailsTab enabled={visited['allowed-emails']} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
